// VOY-1324 / VOY-1340 / VOY-1436 For conditions of exchanges and filtering
import moment from 'moment';
import { OUTBOUND } from 'constants/booking';
import serviceTypeCodes, { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';
import {
  DEPARTS,
  ARRIVES,
  MINUTES,
  BEFORE,
  AFTER,
  FIRST,
  LAST,
  DATE,
  DEPARTURE_DATE,
  ARRIVAL_DATE,
  MINIMUM_CONNECTION_TIME_MINUTES
} from 'constants/summatedFares';

export const buildFaresBeingExchanged = (legsBeingExchanged, itemRefs) => {
  const productsFiltered = legsBeingExchanged?.reduce(
    (acc, { products, ...rest }) => [
      ...acc,
      {
        products: products?.filter((p) => itemRefs?.includes(p.itemRef))?.flat(),
        ...rest
      }
    ],
    []
  );

  return productsFiltered?.filter((productFiltered) => productFiltered.products?.length > 0);
};

export const hasTrainOfService = (segment, service = 'ES') =>
  segment.filter(({ cancelled }) => !cancelled).some(({ serviceType }) => serviceType.code === service);

export const segmentIsEsAndEr = (segment) => {
  const hasEsTrain = hasTrainOfService(segment);
  const hasErTrain = hasTrainOfService(segment, 'ER');
  return hasEsTrain && hasErTrain;
};

export const segmentHasTwoLegs = (segment) => segment?.filter((d) => !d.cancelled)?.length >= 2;

export const segmentHaveDifferentProductCodes = (segment) => {
  const productsCodes = segment
    ?.filter((seg) => !seg.cancelled)
    ?.map((s) =>
      s.products
        ?.filter((product) => !product.cancelled)
        ?.map((p) => p.code)
        ?.flat()
    )
    ?.flat();

  return productsCodes?.some((productCode) => productCode !== productsCodes?.[0]);
};

export const segmentHaveSameProductCodes = (segment) => {
  const productsCodes = segment
    ?.filter((seg) => !seg.cancelled)
    ?.map((s) =>
      s.products
        ?.filter((product) => !product.cancelled)
        ?.map((p) => p.code)
        ?.flat()
    )
    ?.flat();

  return productsCodes?.every((productCode) => productCode === productsCodes?.[0]);
};

export const segmentHasSameProductCodes = (segment) => {
  const productsCodes = segment
    ?.filter((seg) => !seg.cancelled)
    ?.map((s) => s.code)
    ?.flat();

  return productsCodes?.every((productCode) => productCode === productsCodes?.[0]);
};

export const segmentHaveSamePassengers = (segment) => {
  const passengersInSegments = segment
    ?.filter((seg) => !seg.cancelled)
    .map(({ products }) => products?.filter((product) => !product.cancelled)?.map(({ passengerId }) => passengerId));

  if (passengersInSegments.length <= 1) {
    return true;
  }

  const checks = [];

  for (let i = 0; i < passengersInSegments.length; i++) {
    for (let j = i + 1; j < passengersInSegments.length; j++) {
      if (passengersInSegments[i]?.some((passengerId) => passengersInSegments[j]?.includes(passengerId))) {
        checks.push(true);
      } else {
        checks.push(false);
      }
    }
  }

  return checks.some((check) => check === true);
};

export const isESAndERThroughFare = ({ segment = [], itemRefs = '' }) => {
  const paxs = segment.reduce(
    (acc, { products }) => [...acc, ...products.filter(({ itemRef }) => itemRefs === itemRef).flatMap(({ passengerId }) => passengerId)],
    []
  );
  const segmentsForPaxs = segment.reduce(
    (acc, { products }) => [...acc, ...products.filter(({ passengerId }) => paxs.includes(passengerId))],
    []
  );
  return isAThroughFare(segmentsForPaxs) && segmentIsEsAndEr(segment);
};

export const isAThroughFare = (segment) => segmentHasTwoLegs(segment) && segmentHasSameProductCodes(segment);

export const isSegmentASummatedFare = (segment) =>
  segmentHasTwoLegs(segment) && segmentHaveDifferentProductCodes(segment) && segmentHaveSamePassengers(segment);
export const isSegmentAThroughFare = (segment) =>
  segmentHasTwoLegs(segment) && segmentHaveSameProductCodes(segment) && segmentHaveSamePassengers(segment);
export const isSegmentAnESAndErThroughFare = (segment) => isSegmentAThroughFare(segment) && segmentIsEsAndEr(segment);

export const getNSIndividualExchangeDate = ({ segment = [], selectedItemRefs = [] }) => {
  const activeLegs = segment.filter(({ cancelled }) => !cancelled);
  const selectedLegs = activeLegs.filter(({ products }) =>
    products?.some(({ itemRef, cancelled }) => !cancelled && selectedItemRefs.includes(itemRef))
  );
  const hasNS = activeLegs.some(({ serviceType }) => serviceType?.code === serviceTypeCodes.NS);
  const hasEurostar = activeLegs.some(({ serviceType }) =>
    [serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED].includes(serviceType?.code)
  );

  if (selectedLegs.length !== 1 || !hasNS || !hasEurostar || !isSegmentASummatedFare(activeLegs)) return null;

  return activeLegs.find((leg) => !selectedLegs.includes(leg))?.timing?.date || null;
};

export const isExchangingASubsetOfPassengers = (direction, selectedItemRefs) => {
  const productsFiltered = direction?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

  if (productsFiltered?.length === 0) return false;

  const productsPaxs = productsFiltered?.map((productsFiltered) => productsFiltered?.passengerId);

  const numberOfPassengersInSegment = direction
    ?.filter((d) => !d.cancelled)
    ?.map((s) =>
      s.products
        ?.filter((pp) => !pp.cancelled)
        ?.flat()
        ?.map((t) => t.passengerId)
    )
    ?.flat();

  const uniquePaxsInBooking = [...new Set(numberOfPassengersInSegment)];
  const uniquePaxsExchanged = [...new Set(productsPaxs)];

  const exchangingASubsetOfPassengers = uniquePaxsInBooking?.length !== uniquePaxsExchanged?.length;

  return exchangingASubsetOfPassengers;
};

export const selectedItemsAreInADirectFare = (direction, selectedItemRefs) => {
  const productsFiltered = direction?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

  return productsFiltered?.length > 0 && direction?.length === 1;
};

export const selectedItemsAreInAThroughFare = (direction, selectedItemRefs, isThroughFare) => {
  if (isThroughFare) {
    const productsFiltered = direction?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

    return productsFiltered?.length > 0;
  }

  return false;
};

export const selectedItemsAreInASummatedFare = (direction, selectedItemRefs, isSummatedFare) => {
  if (isSummatedFare) {
    const productsFiltered = direction?.map((i) => i.products?.filter((p) => selectedItemRefs?.includes(p.itemRef))?.flat())?.flat();

    return productsFiltered?.length > 0;
  }

  return false;
};

export const findLegPositionBasedOnId = (legs, legId) => legs?.filter((leg) => !leg.cancelled)?.findIndex((l) => l.id === legId);

export const isSameDayEurostarExchangeWithFlexibleConnection = (segment = [], legsBeingExchanged = [], searchDate) => {
  const activeLegs = segment.filter(({ cancelled }) => !cancelled);
  const selectedLeg = legsBeingExchanged[0];
  const connectingLegs = activeLegs.filter(({ id }) => id !== selectedLeg?.id);

  return (
    legsBeingExchanged.length === 1 &&
    EUROSTAR_CARRIERS.includes(selectedLeg?.serviceType?.code) &&
    isSegmentASummatedFare(activeLegs) &&
    connectingLegs.length > 0 &&
    connectingLegs.every(({ serviceType }) => [serviceTypeCodes.SNCB, serviceTypeCodes.NS].includes(serviceType?.code)) &&
    !!selectedLeg?.timing?.date &&
    moment(selectedLeg.timing.date).isSame(moment(), 'day') &&
    !!searchDate &&
    moment(searchDate).isSame(moment(), 'day')
  );
};

export const getLegsAndConditions = (booking, LegsBeingExchanged, direction, searchDate) => {
  if (LegsBeingExchanged?.length > 1) {
    const isMultipleLegsExchange = true;
    return buildExchangeConditions(direction, booking, -1, isMultipleLegsExchange);
  }

  const isMultipleLegsExchange = false;
  const legId = LegsBeingExchanged?.[0]?.id;
  const legIdBeingExchanged = findLegPositionBasedOnId(booking[direction], legId);

  const conditions = buildExchangeConditions(direction, booking, legIdBeingExchanged, isMultipleLegsExchange);

  if (isSameDayEurostarExchangeWithFlexibleConnection(booking[direction], LegsBeingExchanged, searchDate)) {
    const connectionTimeKey = legIdBeingExchanged === 0 ? 'newArrivalTime' : 'newDepartureTime';
    return {
      ...conditions,
      [connectionTimeKey]: undefined,
      newLegsToFilter: { ...conditions.newLegsToFilter, [connectionTimeKey]: undefined }
    };
  }

  return conditions;
};

export const buildTiming = (direction, booking, legId, isMultipleLegsExchange) => {
  const nonCancelledInbound = booking?.inbound?.filter((i) => !i.cancelled);
  const nonCancelledOutbound = booking?.outbound?.filter((o) => !o.cancelled);

  const firstOutboundLeg = nonCancelledOutbound?.[0]?.timing;
  const firstInboundLeg = nonCancelledInbound?.[0]?.timing;
  const lastOutboundLeg = nonCancelledOutbound?.[nonCancelledOutbound?.length - 1]?.timing;
  const lastInboundLeg = nonCancelledInbound?.[nonCancelledInbound?.length - 1]?.timing;

  if (isMultipleLegsExchange) {
    if (direction === OUTBOUND) {
      return {
        newArrivalTime: retrieveTiming(firstInboundLeg, DEPARTS, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, BEFORE)
      };
    }

    return {
      newDepartureTime: retrieveTiming(lastOutboundLeg, ARRIVES, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, AFTER)
    };
  }

  if (legId === -1) return {};

  if (direction === OUTBOUND) {
    if (legId === 0) {
      return {
        newArrivalTime: retrieveTiming(lastOutboundLeg, DEPARTS, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, BEFORE)
      };
    }

    return {
      newArrivalTime: retrieveTiming(firstInboundLeg, DEPARTS, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, BEFORE),
      newDepartureTime: retrieveTiming(firstOutboundLeg, ARRIVES, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, AFTER)
    };
  }

  if (legId === 0) {
    return {
      newArrivalTime: retrieveTiming(lastInboundLeg, DEPARTS, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, BEFORE),
      newDepartureTime: retrieveTiming(lastOutboundLeg, ARRIVES, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, AFTER)
    };
  }

  return {
    newDepartureTime: retrieveTiming(firstInboundLeg, ARRIVES, MINIMUM_CONNECTION_TIME_MINUTES, MINUTES, AFTER)
  };
};

export const retrieveTiming = (leg, departsOrArrive, duration, units, beforeOrAfter) => {
  const formattedDate = buildDate(leg, departsOrArrive);
  const exactTrainTiming =
    beforeOrAfter === BEFORE ? moment(formattedDate)?.subtract(duration, units) : moment(formattedDate)?.add(duration, units);
  return moment(exactTrainTiming)?.format();
};

export const buildExchangeConditions = (direction, booking, legId, isMultipleLegsExchange) => {
  if (isMultipleLegsExchange) {
    const legTiming = buildTiming(direction, booking, -1, isMultipleLegsExchange);
    const multipleLegTimingsObject = {
      newDepartureTime: legTiming?.newDepartureTime,
      newArrivalTime: legTiming?.newArrivalTime,
      filterThroughFare: true
    };

    return direction === OUTBOUND
      ? {
          newLegsToFilter: {
            newArrivalTime: {
              leg: LAST,
              time: ARRIVES,
              condition: BEFORE
            }
          },
          ...multipleLegTimingsObject
        }
      : {
          newLegsToFilter: {
            newDepartureTime: {
              leg: FIRST,
              time: DEPARTS,
              condition: AFTER
            }
          },
          ...multipleLegTimingsObject
        };
  }

  const singleLegTiming = buildTiming(direction, booking, legId, isMultipleLegsExchange);

  const singleLegTimingsObject = {
    newDepartureTime: singleLegTiming?.newDepartureTime,
    newArrivalTime: singleLegTiming?.newArrivalTime,
    filterThroughFare: false
  };

  if (direction === OUTBOUND) {
    if (legId === 0) {
      return {
        newLegsToFilter: {
          newArrivalTime: {
            leg: FIRST,
            time: ARRIVES,
            condition: BEFORE
          }
        },
        ...singleLegTimingsObject
      };
    }

    return {
      newLegsToFilter: {
        newDepartureTime: {
          leg: FIRST,
          time: DEPARTS,
          condition: AFTER
        },
        newArrivalTime: {
          leg: FIRST,
          time: ARRIVES,
          condition: BEFORE
        }
      },
      ...singleLegTimingsObject
    };
  }

  if (legId === 0) {
    return {
      newLegsToFilter: {
        newArrivalTime: {
          leg: FIRST,
          time: ARRIVES,
          condition: BEFORE
        },
        newDepartureTime: {
          leg: FIRST,
          time: DEPARTS,
          condition: AFTER
        }
      },
      ...singleLegTimingsObject
    };
  }

  return {
    newLegsToFilter: {
      newDepartureTime: {
        leg: FIRST,
        time: DEPARTS,
        condition: AFTER
      }
    },
    ...singleLegTimingsObject
  };
};

export const isSummatedFareDateNotValid = (summatedFaresDate) => !summatedFaresDate || !moment(summatedFaresDate).isValid();

export const buildDate = (timing, departsOrArrives, dateOrDepartureDate = DATE) => {
  const departOrArriveTiming = departsOrArrives === DEPARTS ? timing?.departs : timing?.arrives;
  const generateDate = new Date(`${timing?.[dateOrDepartureDate]} ${departOrArriveTiming}`);

  return moment(generateDate).format();
};

const DATE_KEYS_FOR_DEPARTURE = [DEPARTURE_DATE, ARRIVAL_DATE, DATE];
const DATE_KEYS_FOR_ARRIVAL = [ARRIVAL_DATE, DEPARTURE_DATE, DATE];

export const resolveTimingDateKey = (timing, departsOrArrives) => {
  const keysByPreference = departsOrArrives === DEPARTS ? DATE_KEYS_FOR_DEPARTURE : DATE_KEYS_FOR_ARRIVAL;

  return keysByPreference.find((key) => !!timing?.[key]) ?? DEPARTURE_DATE;
};

export const filterSummatedFaresByDepartureOrArrival = (journeys, time, firstOrLastLeg, departsOrArrives, beforeOrAfter) => {
  const legIndex = firstOrLastLeg === FIRST ? 0 : 1;
  const connectionTime = moment(time);

  return journeys.reduce((acc, { fares, ...rest }) => {
    const faresFiltered = fares?.filter((fare) => {
      const legTiming = fare.legs?.length === 1 ? fare.legs?.[0]?.timing : fare.legs?.[legIndex]?.timing;
      const legTime = moment(buildDate(legTiming, departsOrArrives, resolveTimingDateKey(legTiming, departsOrArrives)));

      return beforeOrAfter === BEFORE ? legTime.isSameOrBefore(connectionTime) : legTime.isSameOrAfter(connectionTime);
    });

    if (faresFiltered?.length > 0) {
      acc?.push({ fares: faresFiltered, ...rest });
    }
    return acc;
  }, []);
};

const filterOutThroughFaresTrainsCondition = (legs) => {
  const productCodes = legs.map((l) => l.products?.map((p) => p.code))?.flat();
  return productCodes.some((code) => code !== productCodes[0]);
};

export const filterOutThroughFaresTrains = (journeys) =>
  journeys.reduce((acc, { fares, ...rest }) => {
    const faresFiltered = fares?.filter((fare) => (fare.legs?.length > 1 ? filterOutThroughFaresTrainsCondition(fare.legs) : true));

    if (faresFiltered?.length > 0) {
      acc?.push({ fares: faresFiltered, ...rest });
    }
    return acc;
  }, []);

export const filterSummatedFareTrains = (
  journeys,
  summatedFaresConditions,
  isExchangingAllLegsOfBooking,
  isExchangingASubsetOfPassengers = false
) => {
  if (!summatedFaresConditions || isExchangingAllLegsOfBooking) return journeys;
  const newDepartureTimeObject = summatedFaresConditions.newLegsToFilter?.newDepartureTime;
  const newArrivalTimeObject = summatedFaresConditions.newLegsToFilter?.newArrivalTime;
  const journeysToUse =
    summatedFaresConditions.filterThroughFare && isExchangingASubsetOfPassengers ? filterOutThroughFaresTrains(journeys) : journeys;

  if (summatedFaresConditions.filterThroughFare) {
    if (
      isSummatedFareDateNotValid(summatedFaresConditions.newDepartureTime) &&
      isSummatedFareDateNotValid(summatedFaresConditions.newArrivalTime)
    ) {
      return journeysToUse;
    }

    if (!!newDepartureTimeObject && !isSummatedFareDateNotValid(summatedFaresConditions.newDepartureTime)) {
      return filterSummatedFaresByDepartureOrArrival(
        journeysToUse,
        summatedFaresConditions.newDepartureTime,
        summatedFaresConditions.newLegsToFilter?.newDepartureTime?.leg,
        summatedFaresConditions.newLegsToFilter?.newDepartureTime?.time,
        summatedFaresConditions.newLegsToFilter?.newDepartureTime?.condition
      );
    }

    if (!!newArrivalTimeObject && !isSummatedFareDateNotValid(summatedFaresConditions.newArrivalTime)) {
      return filterSummatedFaresByDepartureOrArrival(
        journeysToUse,
        summatedFaresConditions.newArrivalTime,
        summatedFaresConditions.newLegsToFilter?.newArrivalTime?.leg,
        summatedFaresConditions.newLegsToFilter?.newArrivalTime?.time,
        summatedFaresConditions.newLegsToFilter?.newArrivalTime?.condition
      );
    }

    return journeysToUse;
  }

  if (
    !!newDepartureTimeObject &&
    !isSummatedFareDateNotValid(summatedFaresConditions.newDepartureTime) &&
    !!newArrivalTimeObject &&
    !isSummatedFareDateNotValid(summatedFaresConditions.newArrivalTime)
  ) {
    const filterForDeparture = filterSummatedFaresByDepartureOrArrival(
      journeysToUse,
      summatedFaresConditions.newDepartureTime,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.leg,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.time,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.condition
    );

    return filterSummatedFaresByDepartureOrArrival(
      filterForDeparture,
      summatedFaresConditions.newArrivalTime,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.leg,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.time,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.condition
    );
  }

  if (!!newDepartureTimeObject && !isSummatedFareDateNotValid(summatedFaresConditions.newDepartureTime)) {
    return filterSummatedFaresByDepartureOrArrival(
      journeysToUse,
      summatedFaresConditions.newDepartureTime,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.leg,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.time,
      summatedFaresConditions.newLegsToFilter?.newDepartureTime?.condition
    );
  }

  if (!!newArrivalTimeObject && !isSummatedFareDateNotValid(summatedFaresConditions.newArrivalTime)) {
    return filterSummatedFaresByDepartureOrArrival(
      journeysToUse,
      summatedFaresConditions.newArrivalTime,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.leg,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.time,
      summatedFaresConditions.newLegsToFilter?.newArrivalTime?.condition
    );
  }

  return journeysToUse;
};
